import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AddChartIcon from "@mui/icons-material/Addchart";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AllInclusive from "@mui/icons-material/AllInclusive";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Equalizer from "@mui/icons-material/Equalizer";
import FileUpload from "@mui/icons-material/FileUpload";
import FunctionsIcon from "@mui/icons-material/Functions";
import HandshakeIcon from "@mui/icons-material/Handshake";
import HistoryIcon from "@mui/icons-material/History";
import FilterIcon from "@mui/icons-material/FilterAlt";
import LinkIcon from "@mui/icons-material/Link";
import ListIcon from "@mui/icons-material/List";
import PersonIcon from "@mui/icons-material/Person";
import PlaceIcon from "@mui/icons-material/Place";
import SettingsIcon from "@mui/icons-material/Settings";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import UserIcon from "@mui/icons-material/SupervisedUserCircle";
import Sync from "@mui/icons-material/Sync";
import Timeline from "@mui/icons-material/Timeline";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import blue from "@mui/material/colors/blue";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import * as client from "_graphql-types";
import { RCGApolloProvider } from "frontend/src/components/RCGApolloProvider";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useContext, useMemo } from "react";
import {
  Admin,
  CustomRoutes,
  DataProvider as RaDataProvider,
  RaThemeOptions,
  Resource,
  WithPermissions,
  localStorageStore,
  usePermissions,
} from "react-admin";
import { Route, useLocation } from "react-router-dom";
import config from "../config";
import AssumeUserEdit from "./AssumeUser";
import { AuthContext } from "./Auth/AuthProvider.component";
import { CustomLayout } from "./CustomLayout";
import { useDataProvider } from "./DataProviderContext";
import { DisplayThemeContext } from "./DisplayThemeContext";
import { HistoryLogger } from "./HistoryLogger";
import { TermsOfService } from "./TermsOfService";
import { BusinessApplicationDocuments } from "./company/BusinessApplication";
import AffiliateQuestionaire from "./company/BusinessApplication/AffiliateQuestionaire";
import BusinessApplication from "./company/BusinessApplication/ApplicationForm";
import { ShareClassBulkExtract } from "./shareClass/BulkExtract";
import { RiskFileImport } from "./investmentRisk/RiskFileImport";

import firm from "./firm";
import investment from "./investment";
import deal from "./deal";
import company from "./company";
import person from "./person";
import notes from "./notes";
import document from "./document";
import performance from "./performance";
import performancePrivate from "./performancePrivate";
import aum from "./aum";
import closeDate from "./closeDate";
import shareClass from "./shareClass";
import family from "./family";
import firmDocument from "./firmDocument";
import companyDocument from "./companyDocument";
import companyValuation from "./companyValuation";
import dealDocument from "./dealDocument";
import dealNotes from "./dealNotes";
import investmentDocuments from "./investmentDocument";
import documentQueue from "./documentQueue";
import investmentPeers from "./investmentPeers";
import investmentTerms from "./investmentTerms";
import investmentAxysCode from "./investmentAxysCode";
import investmentCustomAnalytics from "./investmentCustomAnalytics";
import customAnalytics from "./customAnalytics";
import customIndexInvestment from "./customIndexInvestment";
import dataLinks from "./dataLinks";
import financialDataProvider from "./financialDataProvider";
import firmDEI from "./FirmDEI";
import firmFirmDEI from "./firmFirmDEI";
import emailDomainXOrganization from "./emailDomainXOrganization";
import fdpLogin from "./fdpLogin";
import firmFDPLogins from "./firmFDPLogins";
import investmentFDPLogins from "./investmentFDPLogins";
import investmentFirmEmploymentBulk from "./investmentFirmEmploymentBulk";
import links from "./Links";
import organization from "./organization";
import personEducation from "./personEducation";
import personWorkExperience from "./personWorkExperience";
import portalFirm from "./portalFirm";
import portalOrganization from "./portalOrganization";
import portalServiceProviderFirms from "./portalServiceProviderFirms";
import portalSubmitted from "./portalSubmitted";
import portalUser from "./portalUser";
import portfolioConfig from "./portfolioConfig";
import serviceProviderDocument from "./serviceProviderDocument";
import serviceProviderFirm from "./serviceProviderFirm";
import serviceProviderNotes from "./serviceProviderNotes";
import set from "./set";
import user from "./UserAndGroups";
import officeLocation from "./officeLocation";
import institutionEmployment from "./institutionEmployment";
import investmentInstitutions from "./investmentInstitutions";
import firmInstitutions from "./firmInstitutions";
import investmentServiceProviderLinks from "./investmentServiceProviderLinks";
import firmServiceProviderLinks from "./firmServiceProviderLinks";
import investmentPrivateQuartileGroupCategory from "./investmentInvestmentPrivateQuartileGroupCategory";
import prospectiveInvestment from "./prospectiveInvestment";

import { list as investmentRiskList } from "./investmentRisk";
import { list as performanceBackfillList } from "./performanceBackfill";
import { list as performanceMultiInvestmentList } from "./performanceMultiInvestment";
import RiskRefreshList from "./riskRefreshList";
import {
  CapitalFlowEntry,
  ImportPortfolioReturns,
  MonthEndEntry,
  MonthlyCashFlowReport,
  OverTimeReport,
  ShareClassEntry,
} from "./clientInvestments";
import { EarlyEstimates } from "./earlyEstimates/EarlyEstimates";
import { ReportRecipients } from "./reportRecipients/ReportRecipients";

const investmentRisk = { list: investmentRiskList };
const performanceBackfill = { list: performanceBackfillList };
const performanceMultiInvestment = { list: performanceMultiInvestmentList };

export type Permissions = Pick<
  client.GetOneCurrentUserQuery["currentUser"],
  "flags" | "organization"
> & {
  assumedUser?: Pick<
    client.GetOneCurrentUserQuery["currentUser"],
    "flags" | "organization"
  >;
};

const PermissionedAssumeUserEdit = (props: any) => (
  <WithPermissions
    location={props.location}
    render={({ permissions }: { permissions: Permissions }) =>
      permissions?.flags.canAssumeUser ? (
        <AssumeUserEdit {...props} permissions={permissions} />
      ) : (
        <></>
      )
    }
  />
);

declare module "@mui/material/styles" {
  interface TypographyVariants {
    mono: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    mono?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    mono: true;
  }
}

const customTheme = (useDarkTheme: boolean): RaThemeOptions => {
  return createTheme({
    palette: {
      mode: useDarkTheme ? "dark" : "light",
      primary: blue,
      secondary: {
        main: "#2a3fb3",
      },
    },
    ...{
      sidebar: {
        width: 240,
        closedWidth: 50,
      } as any,
    },
    typography: {
      fontFamily: [
        '"Open Sans"',
        "Roboto",
        "Helvetica",
        "Arial",
        "sans-serif",
      ].join(","),
      mono: {
        fontFamily: ['"Roboto Mono"', "Courier", "Monaco", "monospace"].join(),
      },
    },
    components: {
      MuiInputBase: {
        defaultProps: { fullWidth: true, size: "medium" },
      },
      MuiTextField: {
        defaultProps: {
          fullWidth: true,
          size: "medium",
          variant: "outlined",
          inputProps: { autoComplete: false },
        },
      },
      MuiSelect: {
        defaultProps: { fullWidth: true, size: "medium", variant: "outlined" },
      },
      MuiTablePagination: {
        styleOverrides: {
          input: {
            width: "inherit",
          },
        },
      },
      ...{
        RaSelectInput: {
          styleOverrides: {
            root: {
              margin: 0,
            },
          },
        },
      },
      ...{
        RaMultiLevelMenu: {
          styleOverrides: {
            root: {
              backgroundColor: "rgb(250, 250, 250)",
              padding: "0px",
              boxShadow: "inset -2px 0 4px rgba(0, 0, 0, 0.1)",
            },
          },
        },
      },
    },
  });
};

const lightTheme = customTheme(false);
const darkTheme = customTheme(true);

function FallbackComponent() {
  const { signOut } = useContext(AuthContext);
  return (
    <div>
      <button type="button" onClick={signOut}>
        Log Out
      </button>
      <div>
        Something went wrong while loading user. A deployment may be occurring,
        try refreshing in 60 seconds.
      </div>
    </div>
  );
}

const queryCache = new QueryCache();

const mutationCache = new MutationCache({
  onSuccess: (data: any, variables: any, context: any, mutation: any) => {
    queryCache.findAll(variables.resource).map(query => query.invalidate());
    if (variables.resource === "officeLocation") {
      queryCache
        .findAll({ queryKey: ["serviceProviderFirm"] })
        .map(query => query.invalidate());
    }
    if (variables.resource === "investmentPrivateQuartileGroupCategory") {
      // this is to have other queries that use the same data to be invalidated
      // as changing the defaulted value of the quartile group category will affect
      // other queries that use the same data
      queryCache
        .findAll({ queryKey: ["investmentPrivateQuartileGroupCategory"] })
        .map(query => query.invalidate());
    }
    return;
  },
});

/*
https://marmelab.com/react-admin/Caching.html
*/
const queryClient = new QueryClient({
  defaultOptions: {
    /*
     * DO NOT SET A DEFAULT staleTime!!
     * That is the root of all the caching problems, due to
     * RA's (bad) design that updates the getOne cache from
     * a getList response. No way to change that unfortunately.
     * https://github.com/marmelab/react-admin/issues/8702
     * Custom caching is implemented in the data provider.
     
     * This will ensure that a getOne request will be sent on the show pages
     * But the getOne pages(edit/show) will still need to respond to potentially missing 
     * attributes from relating entity requests. User Edit may provide an example for this. If requesting
     * less attributes in for lists.
     * 
     * Removing stale time generally diminishes the chance of displaying old data to the User, as
     * we will always be requesting the latest data from the server. However, this will increase the
     * number of requests to the server, but likely those requests are good to have in an app focused on data capturing.
     * - Sam
    */
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
  mutationCache,
  queryCache,
});

export const CustomAdmin = () => {
  const { useDarkTheme } = useContext(DisplayThemeContext);
  const { dataProvider, showLoginDialog } = useDataProvider();
  const { signOut, auth } = useContext(AuthContext);
  const location = useLocation();

  const shadowFeaturesEnabled = location.search.includes("shadow=true");

  const authProvider = useMemo(
    () => ({
      login: async () => {},
      logout: async () => {
        signOut();
      },
      checkAuth: async () => {
        // Check if user is authenticated
        try {
          const { data } = await dataProvider.getOne<client.CurrentUser>(
            "currentUser",
            { id: "noId" }
          );

          if (!data) {
            throw new Error("Not authenticated");
          }

          return; // User is authenticated
        } catch (error) {
          throw new Error("Not authenticated");
        }
      },
      canAccess: async () => {
        return true; // Always allow access for now
      },
      handleCallback: async () => {},
      getIdentity: async () => {
        const { data } = await dataProvider.getOne<client.CurrentUser>(
          "currentUser",
          { id: "noId" }
        );

        if (!data) {
          throw new Error("Not authenticated");
        }

        return data;
      },
      checkError: async (error: any) => {
        const message = String(error.message);
        if (message.includes("AuthenticationError")) {
          showLoginDialog();
        }
      },
      getPermissions: async () => {
        const { data } = await dataProvider.getOne<client.CurrentUser>(
          "currentUser",
          { id: "idNotNeeded" }
        );

        const permissions: Permissions = {
          flags: data!.flags,
          organization: data!.organization,
        };
        return permissions;
      },
    }),
    [dataProvider, signOut, showLoginDialog]
  );

  if (!auth) return null;

  return (
    <>
      <RCGApolloProvider
        url={config.GRAPHQL_API()}
        getToken={auth.getIdToken}
        FallbackComponent={FallbackComponent}
        webSocketUrl={config.RCG_SIRIUS_WS_URI()}
      >
        <TermsOfService>
          <HistoryLogger />
          <ThemeProvider theme={useDarkTheme ? darkTheme : lightTheme}>
            <Admin
              queryClient={queryClient}
              authProvider={authProvider}
              dataProvider={dataProvider as RaDataProvider}
              layout={CustomLayout}
              theme={useDarkTheme ? darkTheme : lightTheme}
              title="Sirius Data Collection"
              store={localStorageStore("1.1")}
            >
              <CustomRoutes>
                <Route
                  path="/assumeUser"
                  element={<PermissionedAssumeUserEdit />}
                />
                <Route
                  path="/businessApplication"
                  element={<BusinessApplication />}
                />
                <Route
                  path="/businessApplicationAffiliates"
                  element={<AffiliateQuestionaire />}
                />
                <Route
                  path="/businessApplicationDocuments"
                  element={<BusinessApplicationDocuments />}
                />
                <Route
                  path="/shareClass/bulkExtract"
                  element={<ShareClassBulkExtract />}
                />
              </CustomRoutes>
              {(permissions: Permissions) => (
                <>
                  {permissions.flags.businessApplicant &&
                    !permissions.flags.RECFReviewer && (
                      <>
                        <Resource
                          name="company"
                          {...company}
                          list={company.BusinessApplicationRedirect}
                          edit={company.BusinessApplicationEdit}
                          icon={BusinessOutlinedIcon}
                        />
                      </>
                    )}
                  {permissions.flags.isClientServicesUser && (
                    <>
                      <Resource
                        name="portfolioSettings"
                        {...portfolioConfig}
                        options={{
                          label: "Portfolio Settings",
                        }}
                        icon={SettingsIcon}
                      />
                    </>
                  )}
                  {permissions.flags.isServiceProviderManager && (
                    <>
                      <Resource
                        name="serviceProviderFirm"
                        icon={BusinessOutlinedIcon}
                        options={{
                          label: "Service Provider Firms",
                          group: "Service Providers",
                          displayOrder: 1,
                        }}
                        {...serviceProviderFirm}
                      />
                      <Resource
                        name="officeLocation"
                        icon={PlaceIcon}
                        options={{
                          label: "Office Location",
                          group: "Service Providers",
                          displayOrder: 2,
                        }}
                        {...officeLocation}
                      />
                      <Resource
                        name="institutionEmployment"
                        {...institutionEmployment}
                        icon={PersonIcon}
                        options={{
                          group: "Service Providers",
                          label: "Employments",
                          displayOrder: 3,
                        }}
                      />
                      <Resource
                        name="serviceProviderDocument"
                        icon={DescriptionOutlinedIcon}
                        options={{
                          label: "Service Provider Documents",
                          group: "Service Providers",
                          displayOrder: 4,
                        }}
                        {...serviceProviderDocument}
                      />
                      <Resource
                        name="serviceProviderNotes"
                        icon={DescriptionOutlinedIcon}
                        options={{
                          label: "Service Provider Notes",
                          group: "Service Providers",
                          displayOrder: 5,
                        }}
                        {...serviceProviderNotes}
                      />
                      <Resource
                        name="investmentInstitutions"
                        icon={LinkIcon}
                        options={{
                          group: "Service Providers",
                          label: "Investment Link",
                          displayOrder: 6,
                        }}
                        {...investmentInstitutions}
                      />
                      <Resource
                        name="firmInstitutions"
                        icon={LinkIcon}
                        options={{
                          group: "Service Providers",
                          label: "Firm Link",
                          displayOrder: 7,
                        }}
                        {...firmInstitutions}
                      />
                      <Resource
                        name="investmentServiceProviderLinks"
                        {...investmentServiceProviderLinks}
                      />
                      <Resource
                        name="firmServiceProviderLinks"
                        {...firmServiceProviderLinks}
                      />
                    </>
                  )}
                  {permissions.flags.DCAdmin && (
                    <>
                      <Resource
                        {...portalFirm}
                        name="portalFirm"
                        options={{
                          label: "Portal Firms",
                          group: "Portal",
                          displayOrder: 7,
                        }}
                      />
                      <Resource
                        {...portalSubmitted}
                        name="portalSubmitted"
                        options={{
                          label: "Portal Submitted",
                          group: "Portal",
                          displayOrder: 8,
                        }}
                      />
                      <Resource
                        {...portalOrganization}
                        name="portalOrganization"
                        options={{
                          label: "Portal Organizations",
                          group: "Portal",
                          displayOrder: 9,
                        }}
                      />
                      <Resource
                        name="set"
                        {...set}
                        icon={AdminPanelSettingsIcon}
                        options={{
                          label: "Sets",
                          displayOrder: 12,
                        }}
                      />
                      <Resource
                        {...user}
                        name="user"
                        icon={UserIcon}
                        options={{
                          label: "Users & Groups",
                          displayOrder: 12,
                        }}
                      />
                      <Resource
                        {...portalServiceProviderFirms}
                        name="portalServiceProviderFirms"
                      />
                      <Resource
                        {...portalUser}
                        name="portalUser"
                        options={{
                          label: "Portal Users",
                          group: "Portal",
                          displayOrder: 10,
                        }}
                      />
                      <Resource
                        icon={CorporateFareIcon}
                        name="organization"
                        options={{
                          label: "Organizations",
                          displayOrder: 13,
                        }}
                        {...organization}
                      />
                      <Resource name="orgLinks" />
                      <Resource
                        name="links"
                        {...links}
                        options={{
                          label: "Org Links",
                          displayOrder: 13,
                        }}
                      />
                      <Resource
                        name="emailDomainXOrganization"
                        {...emailDomainXOrganization}
                      />
                      <Resource name="userActivity" />
                    </>
                  )}
                  {permissions.flags.isRockCreek && (
                    <>
                      <Resource
                        name="fdpLogin"
                        options={{ label: "Logins", group: "FDP Logins" }}
                        {...fdpLogin}
                        icon={VpnKeyIcon}
                      />
                      <Resource name="firmFDPLogins" {...firmFDPLogins} />
                      <Resource
                        name="investmentFDPLogins"
                        {...investmentFDPLogins}
                      />
                      <Resource
                        name="financialDataProvider"
                        options={{ label: "FDPs", group: "FDP Logins" }}
                        icon={BusinessOutlinedIcon}
                        {...financialDataProvider}
                      />
                      <Resource
                        name="customAnalytics"
                        {...customAnalytics}
                        icon={AddChartIcon}
                        options={{
                          label: "Custom Analytics",
                          displayOrder: 17,
                        }}
                      />
                      {/* Custom pages on Apollo, not the dataProvider; displayOrder keeps the legacy menu order. */}
                      <Resource
                        name="clientShareClassEntry"
                        list={ShareClassEntry}
                        options={{
                          label: "Share Class Entry",
                          group: "Client Investments",
                          displayOrder: 30,
                        }}
                      />
                      <Resource
                        name="clientCapitalFlowEntry"
                        list={CapitalFlowEntry}
                        options={{
                          label: "Capital Flow Entry",
                          group: "Client Investments",
                          displayOrder: 31,
                        }}
                      />
                      <Resource
                        name="clientMonthEndEntry"
                        list={MonthEndEntry}
                        options={{
                          label: "Actual EndBal and Fee Entry",
                          group: "Client Investments",
                          displayOrder: 32,
                        }}
                      />
                      <Resource
                        name="clientImportPortfolioReturns"
                        list={ImportPortfolioReturns}
                        options={{
                          label: "Import Portfolio Returns",
                          group: "Client Investments",
                          displayOrder: 33,
                        }}
                      />
                      <Resource
                        name="clientMonthlyCashFlowReport"
                        list={MonthlyCashFlowReport}
                        options={{
                          label: "Monthly Cash Flow Report",
                          group: "Client Investments",
                          displayOrder: 34,
                        }}
                      />
                      <Resource
                        name="clientOverTimeReport"
                        list={OverTimeReport}
                        options={{
                          label: "Over Time Report",
                          group: "Client Investments",
                          displayOrder: 36,
                        }}
                      />
                      <Resource
                        name="earlyEstimates"
                        list={EarlyEstimates}
                        options={{ label: "Early Estimates", displayOrder: 37 }}
                      />
                      {permissions.flags.isReportingUser && (
                        <Resource
                          name="reportRecipients"
                          list={ReportRecipients}
                          options={{ label: "Report Recipients", displayOrder: 38 }}
                        />
                      )}
                    </>
                  )}
                  {permissions.flags.isSubscriptionUser && (
                    <>
                      <Resource
                        icon={BusinessOutlinedIcon}
                        name="firm"
                        options={{
                          displayOrder: 9,
                        }}
                        {...firm}
                      />
                      <Resource
                        icon={PersonIcon}
                        name="person"
                        options={{ displayOrder: 11 }}
                        {...person}
                      />
                      <Resource
                        name="company"
                        icon={BusinessOutlinedIcon}
                        options={{ displayOrder: 15 }}
                        {...company}
                      />
                      <Resource
                        name="investmentAxysCode"
                        {...investmentAxysCode}
                      />

                      <Resource
                        name="documentQueue"
                        icon={AttachEmailIcon}
                        options={{
                          label: "Archive Assistant",
                          group: "Documents",
                          displayOrder: 6,
                        }}
                        {...documentQueue}
                      />
                      <Resource name="family" {...family} />
                      <Resource name="closeDate" {...closeDate} />
                      <Resource name="agency" />
                      <Resource name="firmDocument" {...firmDocument} />
                      <Resource name="companyDocument" {...companyDocument} />
                      <Resource
                        name="investmentCustomAnalytics"
                        {...investmentCustomAnalytics}
                      />
                      <Resource name="investmentBulk" />
                      <Resource
                        name="personWorkExperience"
                        {...personWorkExperience}
                      />
                      <Resource name="personEducation" {...personEducation} />
                      <Resource name="investmentTags" />
                      <Resource name="tags" />
                      <Resource name="tagClasses" />
                      <Resource name="notes" {...notes} />
                      <Resource name="noteMetaEnum" />
                      <Resource name="serviceProviderDocumentType" />
                      <Resource name="investmentAssignmentOption" />
                      {/* APPEND RESOURCE */}
                      <Resource
                        icon={AccountBalanceOutlinedIcon}
                        name="investment"
                        options={{ displayOrder: 10 }}
                        {...investment}
                      />
                      <Resource
                        name="investmentFirmEmploymentBulk"
                        {...investmentFirmEmploymentBulk}
                      />
                      <Resource
                        name="investmentDocument"
                        {...investmentDocuments}
                      />
                      <Resource name="investmentPeers" {...investmentPeers} />
                      <Resource name="investmentSet" />
                      <Resource name="investmentPeersAuto" />
                      <Resource
                        name="investmentInvestmentPrivateQuartileGroupCategory"
                        {...investmentPrivateQuartileGroupCategory}
                      />
                      <Resource name="shareClass" {...shareClass} />
                      <Resource name="investmentTerms" {...investmentTerms} />
                      <Resource
                        name="performancePrivate"
                        {...performancePrivate}
                      />
                      <Resource
                        name="documentMulti"
                        icon={UploadFileIcon}
                        options={{
                          label: "Multi Create",
                          group: "Documents",
                          displayOrder: 7,
                        }}
                        {...document}
                        create={document.multiCreate}
                        list={document.multiCreate}
                      />
                      <Resource name="document" {...document} />
                      <Resource
                        name="aum"
                        icon={Equalizer}
                        options={{ label: "AUM", displayOrder: 21 }}
                        {...aum}
                      />
                      <Resource
                        {...performance}
                        name="performance"
                        icon={Timeline}
                        options={{
                          label: "Performance",
                          displayOrder: 22,
                        }}
                      />

                      <Resource name="companyValuation" {...companyValuation} />

                      <Resource name="client" />
                      <Resource name="dealStructure" />
                      <Resource name="investmentsFamily" />
                      <Resource name="investmentRiskCategory" />
                      <Resource name="investmentRiskInvestment" />
                      <Resource name="asyncTaskStatus" />
                      <Resource
                        name="prospectiveInvestment"
                        icon={FilterIcon}
                        options={{ label: "Prospective", displayOrder: 16 }}
                        {...prospectiveInvestment}
                      />
                      <Resource
                        name="deal"
                        icon={HandshakeIcon}
                        options={{ label: "Deals", displayOrder: 16 }}
                        {...deal}
                      />
                      <Resource name="dealDocument" {...dealDocument} />
                      <Resource name="dealNotes" {...dealNotes} />
                      <Resource name="dealStructureEnum" />
                      <Resource name="dataLinks" {...dataLinks} />
                    </>
                  )}
                  {permissions.flags.DCAdmin && (
                    <>
                      <Resource
                        name="portalServiceProviderFirms"
                        {...portalServiceProviderFirms}
                      />
                      <Resource name="portalEmailDomainOrganization" />

                      
                      <Resource name="portalFirmTypeEnum" />
                      <Resource name="userXGroups" />
                      <Resource name="application" />
                      <Resource name="adEntriesXApplicationFlags" />
                      <Resource name="adEntriesXApplicationsXSets" />
                      <Resource name="groupShareDefinition" />
                    </>
                  )}
                  {permissions.flags.isRiskUser && (
                    <>
                      <Resource
                        name="importRiskFile"
                        options={{
                          label: "Import Data",
                          group: "Risk Data",
                          displayOrder: 7,
                        }}
                        list={RiskFileImport}
                        icon={FileUpload}
                      />
                      <Resource
                        name="investmentRisk"
                        options={{
                          label: "Manage Data",
                          group: "Risk Data",
                          displayOrder: 7,
                        }}
                        {...investmentRisk}
                        icon={Sync}
                      />
                      <Resource
                        name="riskRefreshList"
                        options={{
                          label: "Daily Refresh List",
                          group: "Risk Data",
                          displayOrder: 20,
                        }}
                        {...RiskRefreshList}
                        icon={ListIcon}
                      />
                    </>
                  )}
                  {permissions.flags.isDataManager && (
                    <>
                      <Resource
                        name="firmDEI"
                        icon={AllInclusive}
                        options={{ label: "Firm DEI", displayOrder: 23 }}
                        {...firmDEI}
                      />
                      <Resource name="firmFirmDEI" {...firmFirmDEI} />
                    </>
                  )}
                  {permissions.flags.DCAdmin && (
                    <>
                      <Resource name="performanceBackfillInvestment" />
                      <Resource
                        {...performanceBackfill}
                        name="performance-backfill"
                        options={{
                          label: "Backfill",
                          group: "Bulk Performance",
                          displayOrder: 8,
                        }}
                        icon={HistoryIcon}
                      />
                      <Resource
                        {...performanceMultiInvestment}
                        name="performance-multi-investment"
                        options={{
                          label: "Multi-Investment",
                          group: "Bulk Performance",
                          displayOrder: 8,
                        }}
                        icon={SsidChartIcon}
                      />
                      <Resource
                        {...customIndexInvestment}
                        name="customIndexInvestment"
                        options={{
                          label: "Custom Index",
                          group: "Bulk Performance",
                          displayOrder: 26,
                        }}
                        icon={FunctionsIcon}
                      />
                      <Resource name="orgLinks" />
                      <Resource name="portalFirmTypeEnum" />
                      <Resource name="customIndexPreview" />
                    </>
                  )}
                  {/* Any One */}
                  <Resource name="applicationFlag" />
                  <Resource name="axysCode" />
                  <Resource name="currencyDenominationEnum" />
                  <Resource name="auditContactTypeEnum" />
                  <Resource name="companyEnum" />
                  <Resource name="contactTypeEnum" />
                  <Resource name="countryEnum" />
                  <Resource name="degreeEnum" />
                  <Resource name="documentTypeEnum" />
                  <Resource name="employeeRoleEnum" />
                  <Resource
                    name="firmEmployment"
                    options={{ label: "Firm Employment" }}
                  />
                  <Resource
                    name="investmentFirmEmployment"
                    options={{ label: "Investment Firm Employment" }}
                  />
                  <Resource name="investmentFirmEmploymentRoleEnum" />
                  <Resource name="schoolEnum" />
                  <Resource name="stateEnum" />
                  <Resource name="strategyEnum" />
                  <Resource name="titleEnum" />
                  <Resource name="investmentListEnum" />
                  <Resource name="peStrategyEnum" />
                  <Resource name="assetClassEnum" />
                  <Resource name="marketEnum" />
                  <Resource name="vehicleTypeEnum" />
                  <Resource name="geographicFocusEnum" />
                  <Resource name="subStrategyEnum" />
                  <Resource name="investmentStructureEnum" />
                  <Resource name="legalStructureEnum" />
                  <Resource name="organizationStructureEnum" />
                  <Resource name="navClassificationEnum" />
                  <Resource name="pricingFrequencyEnum" />
                  <Resource name="institutionTypeEnum" />
                  <Resource name="institutionSelectionStatusEnum" />
                  <Resource name="institutionInstitutionTypeEnum" />
                  <Resource name="measureTypeEnum" />
                  <Resource name="measureEnum" />
                  <Resource name="deiCategory2Enum" />
                  <Resource name="deiCategory1Enum" />
                  <Resource name="geographyEnum" />
                  <Resource name="peFundNumberEnum" />
                  <Resource name="businessObjectEnum" />
                  <Resource name="resultEnum" />
                  <Resource name="stageEnum" />
                  <Resource name="operatingStatusEnum" />
                  <Resource name="companyTypeEnum" />
                  <Resource name="fundingTypeEnum" />
                  <Resource name="statusEnum" />
                  <Resource name="resultEnum" />
                  <Resource name="dealMarketingEnum" />
                  <Resource name="iconEnum" />
                  {/* everyone */}
                  <Resource name="currentUser" />
                  <Resource name="assumeUser" />
                </>
              )}
            </Admin>
          </ThemeProvider>
        </TermsOfService>
      </RCGApolloProvider>
    </>
  );
};
